import { isAxiosError } from "axios";

/**
 * Classification of customer-facing payment outcomes at the call site.
 *
 * `submit-result-key` uses HTTP 400 for two very different things
 * (api/src/routes/submitResultKey.ts):
 *
 * 1. **Card decline** — an expected business outcome. BPoint answered, the
 *    transaction simply did not go through ("Not sufficient funds"). The body
 *    carries `APIResponse` plus the transaction outcome in `TxnResp`.
 * 2. **Malformed `ResultKey`** — `{ message: "Invalid ResultKey" }`, no
 *    `TxnResp`. This rejection is the CWE-22 gate from
 *    BUG-2026-10-28T133726-resultkey-path-traversal-bpoint-idor and is
 *    security-relevant signal, never noise.
 *
 * A blanket "400 is expected" rule would suppress the second, so the decline
 * branch is discriminated by the endpoint's own response contract: it is the
 * only 400 that carries a `TxnResp` object.
 *
 * Fail-safe direction: anything unclassifiable is NOT a decline, so it keeps
 * reporting as an error rather than silently disappearing.
 */

const transactionOutcome = (data: unknown): unknown => {
  if (typeof data !== "object" || data === null) {
    return undefined;
  }
  return (data as { TxnResp?: unknown }).TxnResp;
};

/**
 * True for the 400 that represents an expected card decline.
 *
 * BUG-2026-11-05T010822: such a decline was logged to RUM as an application
 * error, indistinguishable from a genuine integration failure. The 400 stays
 * observable in the RUM resource stream and in pportal-api logs, so dropping
 * the console error loses no signal.
 */
export const isExpectedCardDecline = (error: unknown): boolean => {
  if (!isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }

  // `TxnResp` is always an object here, though BPoint may omit every field —
  // JSON then serializes it as `{}`.
  const txnResp = transactionOutcome(error.response.data);
  return typeof txnResp === "object" && txnResp !== null;
};
